import React, { useState, useEffect } from 'react';
import { db } from '../../src/firebaseConfig';
import { collection, query, where, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import moment from 'moment-timezone';
import styles from './PlatformAccounts.module.css';
import { useNavigate } from 'react-router-dom';
import backgroundImage from '../assets/background2.jpg'; 


function PlatformAccounts() {
    const [platform, setPlatform] = useState('');
    const [platforms, setPlatforms] = useState([]); // Ahora será un estado
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingPlatforms, setLoadingPlatforms] = useState(true); // Loading para plataformas
    const [editingAccount, setEditingAccount] = useState(null);

    const navigate = useNavigate();

    const backgroundStyle = {
        minHeight: '100vh',
        width: '100%',
        margin: 0,
        padding: 0,
        backgroundImage: `linear-gradient(
            rgba(0, 0, 0, 0.60), 
            rgba(0, 0, 0, 0.75)
        ), url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        overflowX: 'hidden' 
    };

    // Función para obtener las plataformas de Firebase
    const fetchPlatforms = async () => {
        try {
            setLoadingPlatforms(true);
            const platformsRef = collection(db, "plataformas");
            const platformsSnapshot = await getDocs(platformsRef);
            
            const platformsData = platformsSnapshot.docs.map((doc) => {
                const data = doc.data();
                return data.nombre; // Solo necesitamos el nombre
            });

            // Ordenar alfabéticamente
            platformsData.sort();
            setPlatforms(platformsData);
        } catch (error) {
            console.error("Error al obtener las plataformas:", error);
            // En caso de error, usar las plataformas por defecto
            const defaultPlatforms = [
                'Netflix', 'Disney plus', 'Paramount', 'Prime Video', 'Crunchyroll',
                'Star plus', 'MAX', 'Spotify', 'Canva Pro', 'Apple TV',
                'IPTV', 'El profe net', 'Smart One IPTV', 'YouTube Premium',
                'PornHub', 'Vix', 'Plex', 'Viki', 'DuplexPlay','Chat-GPT', 'Otro',
            ];
            setPlatforms(defaultPlatforms);
            alert("Error al cargar las plataformas. Se usarán las opciones por defecto.");
        } finally {
            setLoadingPlatforms(false);
        }
    };

    // useEffect para cargar las plataformas al montar el componente
    useEffect(() => {
        fetchPlatforms();
    }, []);

    const fetchAccounts = async () => {
        if (!platform) {
            alert("Por favor, selecciona una plataforma.");
            return;
        }

        setLoading(true);
        try {
            const accountsRef = collection(db, "Cuentas");
            const accountsQuery = query(accountsRef, where("plataforma", "==", platform));
            const accountsSnapshot = await getDocs(accountsQuery);

            if (accountsSnapshot.empty) {
                setAccounts([]);
                alert("No se encontraron cuentas para esta plataforma.");
                return;
            }

            const accountsData = accountsSnapshot.docs.map((doc) => {
                const data = doc.data();
                const fechaCorte = moment(data.fechaCorte).tz('America/Bogota');
                const daysRemaining = fechaCorte.diff(moment().tz('America/Bogota').startOf('day'), 'days');
                return {
                    id: doc.id,
                    codigo: data.codigo,
                    correo: data.correo,
                    contrasena: data.contrasena,
                    pantallas: data.pantallas,
                    propietario: data.propietario || 'No especificado',
                    fechaCorte: fechaCorte.format('YYYY-MM-DD'), // Formato para type="date"
                    estado: daysRemaining > 0 ? (daysRemaining <= 5 ? 'Próximo a Corte' : 'Activo') : 'Vencido',
                };
            });

            setAccounts(accountsData);
        } catch (error) {
            console.error("Error al buscar cuentas:", error);
            alert("Hubo un error al buscar las cuentas. Intenta nuevamente.");
        } finally {
            setLoading(false);
        }
    };

    const deleteAccount = async (id) => {
        const accountToDelete = accounts.find(account => account.id === id);
    
        if (!accountToDelete) {
            alert("No se encontró la cuenta seleccionada.");
            return;
        }
    
        if (accountToDelete.pantallas <= 0) {
            alert("No se puede eliminar una cuenta con 0 pantallas.");
            return;
        }
    
        if (window.confirm("¿Estás seguro de que deseas eliminar esta cuenta?")) {
            try {
                await deleteDoc(doc(db, "Cuentas", id));
                setAccounts(accounts.filter((account) => account.id !== id));
                alert("Cuenta eliminada correctamente.");
            } catch (error) {
                console.error("Error al eliminar la cuenta:", error);
                alert("Hubo un error al eliminar la cuenta.");
            }
        }
    };
    const handleBack = () => {
        navigate('/home');  // Redirigir a la página principal
    };
    
    const startEditing = (account) => {
        setEditingAccount(account);
    };

    const saveChanges = async () => {
        if (!editingAccount) return;
    
        try {
            const accountRef = doc(db, "Cuentas", editingAccount.id);
            await updateDoc(accountRef, {
                codigo: editingAccount.codigo,
                correo: editingAccount.correo,
                contrasena: editingAccount.contrasena,
                pantallas: editingAccount.pantallas,
                fechaCorte: editingAccount.fechaCorte,
                propietario: editingAccount.propietario, 
            });
    
            alert("Cuenta actualizada correctamente.");
            setEditingAccount(null); // Limpia el estado de edición
            fetchAccounts(); // Recarga las cuentas para reflejar los cambios
        } catch (error) {
            console.error("Error al actualizar la cuenta:", error);
            alert("Hubo un error al actualizar la cuenta.");
        }
    };

    return (
        <div style={backgroundStyle}>
            <div className={styles['platform-accounts-container']}>
                <h2 className={styles['title']}>Cuentas Vencidas o próximas a vencer</h2>

                <div className={styles['form-group']}>
                    <select
                        value={platform}
                        onChange={(e) => setPlatform(e.target.value)}
                        className={styles['select']}
                        disabled={loadingPlatforms}
                    >
                        <option value="">
                            {loadingPlatforms ? "-- Cargando Plataformas --" : "-- Selecciona una Plataforma --"}
                        </option>
                        {platforms.map((plat) => (
                            <option key={plat} value={plat}>{plat}</option>
                        ))}
                    </select>
                    <button 
                        onClick={fetchAccounts} 
                        className={styles['searchButton']} 
                        disabled={loading || loadingPlatforms}
                    >
                        {loading ? "Buscando..." : "Buscar"}
                    </button>
                </div>

                {editingAccount && (
                    <div className={styles['edit-form']}>
                        <h3>Editar Cuenta</h3>
                       
                        <label>Correo</label>
                        <input
                            type="email"
                            value={editingAccount.correo}
                            onChange={(e) => setEditingAccount({ ...editingAccount, correo: e.target.value })}
                        />
                        <label>Contraseña</label>
                        <input
                            type="text"
                            value={editingAccount.contrasena}
                            onChange={(e) => setEditingAccount({ ...editingAccount, contrasena: e.target.value })}
                        />

                        <label>Propietario</label> {/* Nuevo campo */}
                            <input
                                type="text"
                                value={editingAccount.propietario}
                                onChange={(e) => setEditingAccount({ ...editingAccount, propietario: e.target.value })}
                            />
          
                        <label>Fecha de Corte</label>
                        <input
                            type="date"
                            value={editingAccount.fechaCorte}
                            onChange={(e) => setEditingAccount({ ...editingAccount, fechaCorte: e.target.value })}
                        />
                        <button onClick={saveChanges} className={styles['searchButton']}>Guardar Cambios
                        </button>
                        <button onClick={() => setEditingAccount(null)} className={styles['transparentFullButton']}>
                            Cancelar
                        </button>

                    </div>
                )}

                {accounts.length > 0 && (
                    <>
                        {/* Cuentas Vencidas */}
                        <h3>Cuentas Vencidas</h3>
                        <table className={styles['accounts-table']}>
                            <thead>
                                <tr>
                                    <th>Código</th>
                                    <th>Correo</th>
                                    <th>Contraseña</th>
                                    <th>Pantallas</th>
                                    <th>Propietario</th>
                                    <th>Fecha de Corte</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {accounts.filter(account => account.estado === 'Vencido').map(account => (
                                    <tr key={account.id} className={styles['vencido']}>
                                        <td>{account.codigo}</td>
                                        <td>{account.correo}</td>
                                        <td>{account.contrasena}</td>
                                        <td>{account.pantallas}</td>
                                        <td>{account.propietario}</td>
                                        <td>{account.fechaCorte}</td>
                                        <td>{account.estado}</td>
                                        <td>
                                            <div className={styles['action-buttons']}>
                                                <button
                                                    onClick={() => startEditing(account)}
                                                    className={styles['action-button']}
                                                >
                                                    Editar
                                                </button>
                                                <button
                                                    onClick={() => deleteAccount(account.id)}
                                                    className={styles['action-button']}
                                                >
                                                    Eliminar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Cuentas Próximas a Corte */}
                        <h3>Cuentas Próximas a Corte</h3>
                        <table className={styles['accounts-table']}>
                            <thead>
                                <tr>
                                    <th>Código</th>
                                    <th>Correo</th>
                                    <th>Contraseña</th>
                                    <th>Pantallas</th>
                                    <th>Propietario</th>
                                    <th>Fecha de Corte</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {accounts.filter(account => account.estado === 'Próximo a Corte').map(account => (
                                    <tr key={account.id} className={styles['proximo']}>
                                        <td>{account.codigo}</td>
                                        <td>{account.correo}</td>
                                        <td>{account.contrasena}</td>
                                        <td>{account.pantallas}</td>
                                        <td>{account.propietario}</td>
                                        <td>{account.fechaCorte}</td>
                                        <td>{account.estado}</td>
                                        <td>
                                            <div className={styles['action-buttons']}>
                                                <button
                                                    onClick={() => startEditing(account)}
                                                    className={styles['action-button']}
                                                >
                                                    Editar
                                                </button>
                                                <button
                                                    onClick={() => deleteAccount(account.id)}
                                                    className={styles['action-button']}
                                                >
                                                    Eliminar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                       {/* Cuentas Activas */}
                        <h3>Cuentas Activas</h3>
                        <table className={styles['accounts-table']}>
                            <thead>
                                <tr>
                                    <th>Código</th>
                                    <th>Correo</th>
                                    <th>Contraseña</th>
                                    <th>Pantallas</th>
                                    <th>Propietario</th>
                                    <th>Fecha de Corte</th>
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {accounts.filter(account => account.estado === 'Activo').map(account => (
                                    <tr key={account.id} className={styles['activo']}>
                                        <td>{account.codigo}</td>
                                        <td>{account.correo}</td>
                                        <td>{account.contrasena}</td>
                                        <td>{account.pantallas}</td>
                                        <td>{account.propietario}</td>
                                        <td>{account.fechaCorte}</td>
                                        <td>{account.estado}</td>
                                        <td>
                                            <div className={styles['action-buttons']}>
                                                <button
                                                    onClick={() => startEditing(account)}
                                                    className={styles['action-button']}
                                                >
                                                    Editar
                                                </button>
                                                <button
                                                    onClick={() => deleteAccount(account.id)}
                                                    className={styles['action-button']}
                                                >
                                                    Eliminar
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}

                {accounts.length === 0 && !loading && (
                    <p className={styles['empty-message']}>
                        No se encontraron cuentas próximas a corte o vencidas.
                    </p>
                )}

                <div className="button-group">
                    <div style={{ width: '100%', marginTop: '30px' }}>
                        <button type="button" onClick={handleBack} className={styles['transparentFullButton']}>
                            Regresar a Inicio 🐻
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PlatformAccounts;